
import React from 'react';
import { formatCurrency, formatDate } from '@/lib/format';
import { Badge } from '@/components/ui/badge';
import { Payment } from '@/services/payment/types';
import { CreditCard, Banknote, FileText, Receipt } from 'lucide-react';
import { ResponsiveTable, TableCellBadge, TableCellText } from '@/components/ui/responsive-table';

interface PaymentListTableProps {
  payments: Payment[];
}

const PAYMENT_METHOD_ICONS = {
  stripe: CreditCard,
  cash: Banknote,
  check: FileText,
  money_order: Receipt,
  manual_card: CreditCard,
};

const PAYMENT_METHOD_LABELS = {
  stripe: 'Credit Card',
  cash: 'Cash',
  check: 'Check',
  money_order: 'Money Order',
  manual_card: 'Manual Card',
};

const PaymentListTable: React.FC<PaymentListTableProps> = ({ payments }) => {
  const renderPaymentMethod = (payment: Payment) => {
    const paymentMethod = payment.payment_method || 'stripe';
    const Icon = PAYMENT_METHOD_ICONS[paymentMethod as keyof typeof PAYMENT_METHOD_ICONS] || CreditCard;
    const label = PAYMENT_METHOD_LABELS[paymentMethod as keyof typeof PAYMENT_METHOD_LABELS] || 'Unknown';
    
    const getMethodColor = () => {
      switch (paymentMethod) {
        case 'stripe':
          return 'text-blue-600';
        case 'cash':
          return 'text-green-600';
        case 'check':
          return 'text-purple-600';
        case 'money_order':
          return 'text-orange-600';
        case 'manual_card':
          return 'text-indigo-600';
        default:
          return 'text-gray-600';
      }
    };

    return (
      <div className="flex items-center gap-2">
        <Icon className={`h-4 w-4 ${getMethodColor()}`} />
        <span className="text-sm">
          {label}
          {payment.payment_method === 'check' && payment.check_number && (
            <span className="text-gray-500"> #{payment.check_number}</span>
          )}
        </span>
      </div>
    );
  };

  const renderReceipt = (payment: Payment) => {
    if (payment.receipt_url) {
      return (
        <a
          href={payment.receipt_url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800 hover:underline text-sm"
        >
          View Receipt
        </a>
      );
    } else if (payment.receipt_number) {
      return (
        <span className="text-gray-600 font-mono text-xs">
          {payment.receipt_number}
        </span>
      );
    }
    return <span className="text-gray-500 text-sm">N/A</span>;
  };

  const columns = [
    {
      key: 'family_name',
      header: 'Family',
      cell: (payment: Payment) => (
        <TableCellText 
          primary={payment.family_name || 'Unknown Family'} 
        />
      ),
      priority: 'high' as const
    },
    {
      key: 'amount',
      header: 'Amount',
      cell: (payment: Payment) => (
        <span className="font-semibold text-lg">
          {formatCurrency(payment.amount_cents ? payment.amount_cents / 100 : 0)}
        </span>
      ),
      priority: 'high' as const
    },
    {
      key: 'payment_method',
      header: 'Payment Method',
      cell: (payment: Payment) => renderPaymentMethod(payment),
      priority: 'medium' as const
    },
    {
      key: 'date',
      header: 'Date',
      cell: (payment: Payment) => (
        <span className="text-sm text-gray-600">
          {formatDate(payment.paid_at || '')}
        </span>
      ),
      priority: 'medium' as const
    },
    {
      key: 'status',
      header: 'Status',
      cell: (payment: Payment) => (
        <TableCellBadge variant={payment.status === 'succeeded' ? 'default' : 'secondary'}>
          {payment.status || 'Processing'}
        </TableCellBadge>
      ),
      priority: 'high' as const
    },
    {
      key: 'receipt',
      header: 'Receipt',
      cell: (payment: Payment) => renderReceipt(payment),
      priority: 'low' as const,
      hideOnMobile: true
    }
  ];

  return (
    <ResponsiveTable
      data={payments}
      columns={columns}
      cardTitle={(payment) => payment.family_name || 'Unknown Family'}
      cardSubtitle={(payment) => formatDate(payment.paid_at || '')}
      emptyMessage="No payments found"
    />
  );
};

export default PaymentListTable;
